// Regelset-Verwaltung (nur admin, s. AuftragsbestaetigungView.js): Liste aller Regelsets
// (GET /rulesets), gruppiert nach Lieferant, mit Versionen + Aktiv-Badge. Aktionen aktivieren/
// deaktivieren/loeschen (Confirm-Dialog). "Neue Version"/"Bearbeiten" oeffnet den JSON-Editor
// (AbRulesetEditor.js); "Im Mapping-Editor bearbeiten" (Welle 2 "UI-Paritaet", Agent FE-3) oeffnet
// stattdessen den visuellen Feld-Mapping-Editor (mapping/AbMappingEditor.js) — verlangt dort ein
// Testdokument (Gate innerhalb der Komponente, s. dort). Muster: AbDocumentList.js (Card, 3-State,
// ConfirmDialog aus ui.js).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Plus, RefreshCw, Pencil, CheckCircle2, Circle, Trash2, Layers, Wand2 } from 'lucide-react';
import { Card, SectionTitle, Chip, btnGhost, btnPrimary, iconBtnCls, ConfirmDialog, fmtDate } from './ui';
import AbRulesetEditor from './AbRulesetEditor';
import AbMappingEditor from '../mapping/AbMappingEditor';
import { listRulesets, getRuleset, activateRuleset, deactivateRuleset, deleteRuleset } from '../api';

const AbRulesetAdmin = ({ user, isDarkMode, t, onBack, onOpenDocument, showSuccess, showError }) => {
  const [rulesets, setRulesets] = useState(null); // null = laedt noch
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // Regelset-Meta
  const [editorState, setEditorState] = useState(null); // null | { mode: 'new'|'edit'|'mapping', ruleset? }

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const rows = await listRulesets(user);
      setRulesets(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setLoadError(e.message);
      setRulesets([]);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of rulesets || []) {
      const key = r.supplierId || '—';
      if (!m.has(key)) m.set(key, { supplierId: r.supplierId, supplierName: r.supplierName, versions: [] });
      m.get(key).versions.push(r);
    }
    return Array.from(m.values());
  }, [rulesets]);

  const openNew = () => setEditorState({ mode: 'new' });
  const openEdit = async (meta) => {
    try {
      const full = await getRuleset(user, meta.id);
      setEditorState({ mode: 'edit', ruleset: full });
    } catch (e) {
      showError?.(e.message);
    }
  };
  const openMapping = async (meta) => {
    try {
      const full = await getRuleset(user, meta.id);
      setEditorState({ mode: 'mapping', ruleset: full });
    } catch (e) {
      showError?.(e.message);
    }
  };

  const doActivate = async (meta) => {
    setBusyId(meta.id);
    try {
      await activateRuleset(user, meta.id);
      showSuccess?.(t('ab.rulesetActivatedMsg'));
      await load();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const doDeactivate = async (meta) => {
    setBusyId(meta.id);
    try {
      await deactivateRuleset(user, meta.id);
      showSuccess?.(t('ab.rulesetDeactivatedMsg'));
      await load();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await deleteRuleset(user, confirmDelete.id);
      showSuccess?.(t('ab.rulesetDeletedMsg'));
      setConfirmDelete(null);
      await load();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (editorState?.mode === 'mapping') {
    return (
      <AbMappingEditor
        user={user} isDarkMode={isDarkMode} t={t}
        ruleset={editorState.ruleset}
        onBack={() => { setEditorState(null); load(); }}
        onOpenDocument={onOpenDocument}
        showSuccess={showSuccess} showError={showError}
      />
    );
  }

  if (editorState) {
    return (
      <AbRulesetEditor
        user={user} isDarkMode={isDarkMode} t={t}
        mode={editorState.mode} ruleset={editorState.ruleset}
        onClose={() => setEditorState(null)}
        onSaved={() => { setEditorState(null); load(); }}
        showSuccess={showSuccess} showError={showError}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white/85' : 'text-slate-800'}`}>{t('ab.rulesetsTitle')}</h2>
        <div className="flex-1" />
        <button onClick={load} className={btnGhost(isDarkMode)} title={t('ab.refresh')}><RefreshCw className="w-4 h-4" /></button>
        <button onClick={openNew} className={btnPrimary(isDarkMode)}>
          <span className="flex items-center gap-2"><Plus className="w-4 h-4" />{t('ab.rulesetNewVersion')}</span>
        </button>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          isDarkMode={isDarkMode} t={t} tone="danger" busy={busyId === confirmDelete.id}
          title={t('ab.rulesetConfirmDeleteTitle')}
          message={t('ab.rulesetConfirmDeleteMsg', { supplier: confirmDelete.supplierName || confirmDelete.supplierId, version: confirmDelete.version })}
          onConfirm={doDelete} onCancel={() => setConfirmDelete(null)}
        />
      )}

      {loadError && (
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.rulesetsLoadError')}: {loadError}</p>
          <button onClick={load} className={`${btnGhost(isDarkMode)} mt-3`}>{t('ab.refresh')}</button>
        </Card>
      )}

      {!loadError && rulesets === null && (
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.loading')}</p>
      )}

      {!loadError && rulesets !== null && groups.length === 0 && (
        <Card isDarkMode={isDarkMode}>
          <p className={`text-sm text-center py-6 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.rulesetsEmpty')}</p>
        </Card>
      )}

      {groups.map((g) => (
        <Card key={g.supplierId || 'unknown'} isDarkMode={isDarkMode}>
          <SectionTitle isDarkMode={isDarkMode}>
            <span className="flex items-center gap-2 flex-wrap">
              <Layers className="w-4 h-4 flex-shrink-0" />
              {g.supplierName || g.supplierId || t('ab.supplierUnknown')}
              <span className={`font-mono text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{g.supplierId}</span>
            </span>
          </SectionTitle>
          <div className="space-y-2">
            {g.versions.map((v) => (
              <div key={v.id} className={`flex items-center gap-3 flex-wrap rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/[0.04]' : 'bg-slate-50'}`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{t('ab.rulesetVersion', { v: v.version })}</span>
                <Chip tone={v.isActive ? 'green' : 'slate'} isDarkMode={isDarkMode}>
                  {v.isActive ? t('common.active') : t('common.inactive')}
                </Chip>
                <span className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{fmtDate(v.createdAt)}</span>
                <div className="flex-1" />
                <button disabled={busyId === v.id} onClick={() => openEdit(v)} className={iconBtnCls(isDarkMode)} title={t('ab.rulesetEdit')}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button disabled={busyId === v.id} onClick={() => openMapping(v)} className={iconBtnCls(isDarkMode)} title={t('ab.map.openEditorButton')}>
                  <Wand2 className="w-4 h-4" />
                </button>
                {v.isActive ? (
                  <button disabled={busyId === v.id} onClick={() => doDeactivate(v)} className={iconBtnCls(isDarkMode)} title={t('ab.rulesetDeactivate')}>
                    <Circle className="w-4 h-4" />
                  </button>
                ) : (
                  <button disabled={busyId === v.id} onClick={() => doActivate(v)} className={iconBtnCls(isDarkMode)} title={t('ab.rulesetActivate')}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
                <button disabled={busyId === v.id || v.isActive} onClick={() => setConfirmDelete(v)}
                  className={iconBtnCls(isDarkMode, 'danger')} title={v.isActive ? t('ab.rulesetDeleteActiveHint') : t('common.delete')}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AbRulesetAdmin;
